// Date/time utilities for recurring event display.
// Uses IANA time zones (Europe/Zurich, America/Sao_Paulo) to auto-handle DST.

const ZURICH_TZ = "Europe/Zurich";
const BRAZIL_TZ = "America/Sao_Paulo";

// Read date parts of a Date instance interpreted in a given IANA time zone.
const partsIn = (date, timeZone) => {
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (t) => p.find((x) => x.type === t)?.value;
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")),
    minute: Number(get("minute")),
    weekdayLabel: get("weekday"),
  };
};

// Return the UTC instant when the given clock time (h:m) occurs in the given IANA zone
// on the given civil date (year, month 0-based, day). Handles DST transitions.
const instantAtZoneWallTime = (year, month, day, hour, minute, timeZone) => {
  // Start with the naive UTC guess, then compute the offset difference.
  let d = new Date(Date.UTC(year, month, day, hour, minute));
  const seen = partsIn(d, timeZone);
  const seenUtcMinutes = seen.hour * 60 + seen.minute;
  const wantedMinutes = hour * 60 + minute;
  const deltaMin = wantedMinutes - seenUtcMinutes;
  d = new Date(d.getTime() + deltaMin * 60 * 1000);
  return d;
};

// isoWeekday: Monday=1..Sunday=7 (matches our backend model: 1=Tue, 3=Thu, 5=Sat, but ISO Mon=1 so Tue=2)
// However backend uses weekday: 0=Mon..6=Sun.  Convert: jsWeekday = (weekday + 1) % 7 -> Sun=0..Sat=6 JS
// weekday 1 = Tuesday (backend), JS Date.getDay(): Sun=0 Mon=1 Tue=2 Wed=3 Thu=4 Fri=5 Sat=6.
// Backend `weekday` (0=Mon..6=Sun) maps to JS getDay: (backendWeekday + 1) % 7.

// Civil date "YYYY-MM-DD" of an instant in Zurich (to compare with cancelled dates)
const zurichISODate = (instant) => {
  const p = partsIn(instant, ZURICH_TZ);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
};

// Compute the next occurrence for a WEEKLY event with given backend weekday and Bern local time HH:MM.
// Returns a Date instant (UTC). Occurrences whose Zurich civil date is in `cancelled` are skipped
// (date deactivated in the workers area disappears from the public agenda).
export const nextWeeklyOccurrence = (backendWeekday, timeStr, now = new Date(), cancelled = new Set()) => {
  const [hStr, mStr] = timeStr.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  const jsTargetDay = (backendWeekday + 1) % 7; // JS getDay

  // Get today's civil date in Zurich
  const today = partsIn(now, ZURICH_TZ);
  const todayDate = new Date(Date.UTC(today.year, today.month - 1, today.day));
  const todayJsDay = todayDate.getUTCDay();

  let daysAhead = (jsTargetDay - todayJsDay + 7) % 7;
  // If it's the same weekday, check if the event time is still in the future today
  if (daysAhead === 0) {
    const todayEventInstant = instantAtZoneWallTime(today.year, today.month - 1, today.day, h, m, ZURICH_TZ);
    if (now.getTime() > todayEventInstant.getTime()) {
      daysAhead = 7;
    }
  }

  let target = new Date(todayDate.getTime() + daysAhead * 86400000);
  let instant = instantAtZoneWallTime(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate(), h, m, ZURICH_TZ);
  for (let i = 0; i < 60 && cancelled.has(zurichISODate(instant)); i++) {
    target = new Date(target.getTime() + 7 * 86400000);
    instant = instantAtZoneWallTime(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate(), h, m, ZURICH_TZ);
  }
  return instant;
};

// Compute next "first Saturday of the month" occurrence (skips cancelled dates).
export const nextFirstSatOfMonth = (timeStr, now = new Date(), cancelled = new Set()) => {
  const [hStr, mStr] = timeStr.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  const t = partsIn(now, ZURICH_TZ);
  const findFirstSat = (year, month0) => {
    for (let day = 1; day <= 7; day++) {
      const d = new Date(Date.UTC(year, month0, day));
      if (d.getUTCDay() === 6) return { year, month0, day };
    }
    return null;
  };
  let fs = findFirstSat(t.year, t.month - 1);
  let instant = instantAtZoneWallTime(fs.year, fs.month0, fs.day, h, m, ZURICH_TZ);
  for (let i = 0; i < 36 && (now.getTime() > instant.getTime() || cancelled.has(zurichISODate(instant))); i++) {
    const next = new Date(Date.UTC(fs.year, fs.month0 + 1, 1));
    fs = findFirstSat(next.getUTCFullYear(), next.getUTCMonth());
    instant = instantAtZoneWallTime(fs.year, fs.month0, fs.day, h, m, ZURICH_TZ);
  }
  return instant;
};

// Format helpers — always show local (zoned) times/dates and pretty full date labels.
export const formatZonedTime = (instant, timeZone) =>
  new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(instant);

// Locale map for our 5 site languages -> BCP 47 tags
export const LOCALES = {
  de: "de-CH",
  pt: "pt-BR",
  fr: "fr-CH",
  it: "it-CH",
  en: "en-GB",
};

export const weekdayLongName = (backendWeekday, lang) => {
  const locale = LOCALES[lang] || "en-GB";
  const jsDay = (backendWeekday + 1) % 7;
  // Anchor on a known date whose UTC weekday matches jsDay
  const anchor = new Date(Date.UTC(2024, 0, 7 + jsDay)); // 2024-01-07 was Sunday (jsDay 0)
  return new Intl.DateTimeFormat(locale, { weekday: "long", timeZone: "UTC" }).format(anchor);
};

export const formatWeekdayLong = (instant, timeZone, locale) => {
  const s = new Intl.DateTimeFormat(locale, { timeZone, weekday: "long" }).format(instant);
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const formatDateOnly = (instant, timeZone, locale) =>
  new Intl.DateTimeFormat(locale, {
    timeZone,
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(instant);

// Split "Sábado, 19 de setembro de 2026" -> ["Sábado", "19 de setembro de 2026"]
const splitDateNote = (note) => {
  const i = note.indexOf(", ");
  if (i === -1) return { weekdayLabel: "", dateOnly: note };
  return { weekdayLabel: note.slice(0, i), dateOnly: note.slice(i + 2) };
};

export const resolveEventTimes = (event, lang, now = new Date()) => {
  const locale = LOCALES[lang] || "en-GB";
  if (event.recurrence === "once" || event.recurrence === "yearly") {
    const note = event.date_note?.[lang] || event.date_note?.pt || "";
    const { weekdayLabel, dateOnly } = splitDateNote(note);
    return {
      isOnce: true,
      weekdayLabel,
      dateLabel: dateOnly,
      timeCh: event.time,
      timeBr: event.time_br,
      sortKey: event.date ? `${event.date}T${event.time || "00:00"}` : "9999",
    };
  }

  const cancelled = new Set(event.cancelled_dates || []);
  let instant;
  if (event.recurrence === "monthly-first-sat") {
    instant = nextFirstSatOfMonth(event.time, now, cancelled);
  } else {
    // weekly (default)
    instant = nextWeeklyOccurrence(event.weekday, event.time, now, cancelled);
  }

  return {
    isOnce: false,
    weekdayLabel: formatWeekdayLong(instant, ZURICH_TZ, locale),
    dateLabel: formatDateOnly(instant, ZURICH_TZ, locale),
    timeCh: formatZonedTime(instant, ZURICH_TZ),
    timeBr: formatZonedTime(instant, BRAZIL_TZ),
    sortKey: instant.toISOString(),
  };
};
